import type { Membership } from "../../../lib/queries/memberships";
import type { AgeCategoryTable, RuleSetDocument } from "../../../lib/queries/rules";
import { ageCategoryMatches, ageOn } from "../../../lib/entryChecks";
import { seasonOrdinal } from "../../../lib/seasonPhase";
import type { ID } from "../../../lib/id";

/**
 * Pure helpers behind the transfer revize (TransferRevize). Grouping is
 * PRESENTATION only: it decides under which heading a candidate's chip
 * renders, never whether anything may be submitted — the server keeps all
 * validation, so a drift against internal/service/rules/age.go cannot
 * corrupt anything. The age arithmetic itself is not re-implemented here;
 * it comes from lib/entryChecks, the one existing mirror of age.go.
 */

/** Below this many candidates the revize is a single chip row — group
 * headers for eight people are ceremony, not structure. */
export const REVIZE_GROUPS_FROM = 10;
/** From this many candidates a name is faster typed than found. */
export const REVIZE_SEARCH_FROM = 20;

export const COACHES_GROUP_LABEL = "Trenéři a asistenti";
/** Candidates neither path can place: no birth date at all. With a ladder
 * it also collects anyone outside every band. */
export const UNMATCHED_GROUP_LABEL = "Ostatní";

/** Lowercased, diacritics-stripped form — „rican" finds „Říčná". */
export function normalizeForSearch(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/** Both name orders match, so „nová ter" and „tereza n" each work. */
export function candidateMatches(m: Membership, query: string): boolean {
  const q = normalizeForSearch(query.trim());
  if (!q) return true;
  const p = m.person;
  const haystack = normalizeForSearch(
    `${p.first_name} ${p.last_name} ${p.last_name} ${p.first_name}`,
  );
  return haystack.includes(q);
}

/**
 * One rule set can hold several age ladders (each discipline names its
 * own); the revize wants the one most disciplines are judged by.
 */
export function pickAgeTable(doc: RuleSetDocument): AgeCategoryTable | null {
  const withCats = doc.age_tables.filter((t) => (t.categories?.length ?? 0) > 0);
  if (withCats.length === 0) return null;
  const refs = new Map<ID, number>();
  for (const d of doc.disciplines) {
    refs.set(d.age_category_table_id, (refs.get(d.age_category_table_id) ?? 0) + 1);
  }
  return withCats.reduce((best, t) =>
    (refs.get(t.id) ?? 0) > (refs.get(best.id) ?? 0) ? t : best,
  );
}

/**
 * The date candidate ages are computed against. A fixed-date ladder (ČNSMS:
 * 31. 12.) only reads the year off it; for an at-competition ladder,
 * mid-season is the honest approximation, since the season has no single
 * competition day. Of „2026/2027" the LATER year is the one competitions
 * run in, so that is the year taken.
 */
export function seasonReferenceDate(seasonCode: string): string {
  // The current year for a code with no year in it. seasonOrdinal answers
  // null there on purpose — deciding whether a season is closed on a
  // naming convention is not allowed — but an age has to be computed
  // against SOMETHING, and today is the least wrong guess available.
  return `${seasonOrdinal(seasonCode) ?? new Date().getFullYear()}-06-30`;
}

/**
 * Year of birth, as the group heading when no ladder is available.
 *
 * Exact by construction: it needs no rule set, no reference date and no
 * arithmetic, so unlike an age band it cannot be wrong. That is the point
 * of the fallback — a season whose rules nobody has designated yet gets a
 * structure that states a fact, instead of one that quietly borrows another
 * season's bands.
 */
export function birthYear(birthDate: string): string {
  return birthDate.slice(0, 4);
}

export interface RevizeGroup {
  key: string;
  label: string;
  /**
   * The age category's palette slot, when the group IS one. Empty for the
   * two groups that are not an age category at all — the unplaceable
   * candidates and the bench crew — so those keep the plain badge rather
   * than borrowing a colour that means an age.
   */
  color: string;
  members: Membership[];
}

/**
 * Splits candidates into the groups a leader actually thinks in: the rule
 * set's age categories in ladder order, then anyone the ladder cannot
 * place, then coaches/assistants — people whose roles include no
 * `athlete` belong to the bench crew regardless of age. Empty groups are
 * dropped; members sort by surname so a group scans like a roster.
 */
export function groupCandidates(
  candidates: Membership[],
  table: AgeCategoryTable | null,
  referenceDate: string,
): RevizeGroup[] {
  const cats = [...(table?.categories ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const coaches: Membership[] = [];
  const unmatched: Membership[] = [];
  const byCat = new Map<ID, Membership[]>();
  const byYear = new Map<string, Membership[]>();

  for (const m of candidates) {
    const competes = m.roles.includes("athlete");
    if (!competes && (m.roles.includes("coach") || m.roles.includes("assistant"))) {
      coaches.push(m);
      continue;
    }
    if (!m.person.birth_date) {
      unmatched.push(m);
      continue;
    }
    if (!table) {
      // No ladder: group by ročník instead of inventing bands.
      const y = birthYear(m.person.birth_date);
      const list = byYear.get(y);
      if (list) list.push(m);
      else byYear.set(y, [m]);
      continue;
    }
    const cat = cats.find((c) =>
      ageCategoryMatches(c, ageOn(m.person.birth_date, table, referenceDate)),
    );
    if (!cat) {
      unmatched.push(m);
      continue;
    }
    const list = byCat.get(cat.id);
    if (list) list.push(m);
    else byCat.set(cat.id, [m]);
  }

  const bySurname = (a: Membership, b: Membership) =>
    a.person.last_name.localeCompare(b.person.last_name, "cs") ||
    a.person.first_name.localeCompare(b.person.first_name, "cs");

  const groups: RevizeGroup[] = [];
  for (const c of cats) {
    const members = byCat.get(c.id);
    if (members?.length)
      groups.push({ key: `cat:${c.id}`, label: c.name, color: c.color, members });
  }
  // Youngest first, the same direction the ladder runs, so the two
  // groupings read the same way round.
  for (const y of [...byYear.keys()].sort().reverse()) {
    groups.push({ key: `year:${y}`, label: y, color: "", members: byYear.get(y)! });
  }
  if (unmatched.length) {
    groups.push({
      key: "other",
      label: UNMATCHED_GROUP_LABEL,
      color: "",
      members: unmatched,
    });
  }
  if (coaches.length) {
    groups.push({ key: "coaches", label: COACHES_GROUP_LABEL, color: "", members: coaches });
  }
  for (const g of groups) g.members.sort(bySurname);
  return groups;
}
